export const GLOBAL_ERROR_RATE_OBJECTIVE = 2;
const PRIORITY_FLUX_SHARE_THRESHOLD = 50;
const PRIORITY_MIN_ERROR_COUNT = 5;
const PRIORITY_MIN_GLOBAL_ERROR_COUNT = 10;

const computeGlobalErrorRate = (dataVolume) => {
  if (!dataVolume) return 0;
  let totalWaste = 0;
  let totalError = 0;
  Object.values(dataVolume).forEach((entry) => {
    totalWaste += entry?.totalWasteVolume ?? 0;
    totalError += entry?.totalVolumeSortingError ?? 0;
  });
  return totalWaste > 0 ? (totalError / totalWaste) * 100 : 0;
};

const countTrackedObjects = (dataSortingError) => {
  if (!dataSortingError) return 0;
  return Object.entries(dataSortingError).filter(
    ([material, entries]) =>
      material !== "unknown" &&
      Array.isArray(entries) &&
      entries.some((entry) => (entry?.countAnomaly ?? 0) > 0),
  ).length;
};

const countCharacterizedObjects = (monthlyCaracterisation) => {
  if (!Array.isArray(monthlyCaracterisation)) return 0;
  const materials = new Set();
  monthlyCaracterisation.forEach((entry) => {
    Object.entries(entry?.wasteData ?? {}).forEach(([material, count]) => {
      if (count > 0) materials.add(material);
    });
  });
  return materials.size;
};

const findMostImpactedFlux = (dataResult) => {
  if (!dataResult || Object.keys(dataResult).length === 0) return null;

  const fluxes = Object.entries(dataResult).map(([label, fluxData]) => ({
    label,
    count: fluxData?.countAnomalies ?? 0,
  }));
  const total = fluxes.reduce((sum, flux) => sum + flux.count, 0);
  if (total === 0) return null;

  const top = fluxes.sort((a, b) => b.count - a.count)[0];
  return {
    ...top,
    share: (top.count / total) * 100,
  };
};

const findDominantCauseForFlux = (dataSortingError, fluxLabel) => {
  if (!dataSortingError || !fluxLabel) return null;

  const materialCounts = {};
  Object.entries(dataSortingError).forEach(([material, entries]) => {
    if (material === "unknown" || !Array.isArray(entries)) return;
    const fluxCount = entries
      .filter((entry) => entry.flux === fluxLabel)
      .reduce((sum, entry) => sum + (entry.countAnomaly ?? 0), 0);
    if (fluxCount > 0) materialCounts[material] = fluxCount;
  });

  const sorted = Object.entries(materialCounts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] ?? null;
};

export const buildQualityInsights = (statisticsData) => {
  const empty = {
    errorCount: 0,
    globalErrorRate: 0,
    trackedObjectsCount: 0,
    characterizedObjectsCount: 0,
    mostImpactedFlux: null,
    priorityAction: null,
  };

  if (!statisticsData) return empty;

  const dataResult = statisticsData.dataResult ?? {};
  const dataVolume = statisticsData.dataVolume ?? {};
  const dataSortingError = statisticsData.dataSortingError ?? {};
  const monthlyCaracterisation = statisticsData.monthlyCaracterisation ?? [];

  const mostImpactedFlux = findMostImpactedFlux(dataResult);
  const globalErrorRate = computeGlobalErrorRate(dataVolume);
  const dominantCause = mostImpactedFlux
    ? findDominantCauseForFlux(dataSortingError, mostImpactedFlux.label)
    : null;

  const priorityAction =
    mostImpactedFlux &&
    mostImpactedFlux.share >= PRIORITY_FLUX_SHARE_THRESHOLD &&
    (mostImpactedFlux.count >= PRIORITY_MIN_ERROR_COUNT ||
      (statisticsData.countAnomalies ?? 0) >= PRIORITY_MIN_GLOBAL_ERROR_COUNT ||
      globalErrorRate >= GLOBAL_ERROR_RATE_OBJECTIVE)
      ? {
          fluxLabel: mostImpactedFlux.label,
          share: Number(mostImpactedFlux.share.toFixed(1)),
          count: mostImpactedFlux.count,
          dominantCause,
        }
      : null;

  return {
    errorCount: statisticsData.countAnomalies ?? 0,
    globalErrorRate,
    trackedObjectsCount: countTrackedObjects(dataSortingError),
    characterizedObjectsCount: countCharacterizedObjects(
      monthlyCaracterisation,
    ),
    mostImpactedFlux,
    priorityAction,
  };
};
